import { PlayerError } from '@foundation-player/error-codes';
import { getLogger } from '@foundation-player/logger';
import { isVodafoneVTV } from '@foundation-player/platform-check';
import type { LoaderConfig } from '@foundation-player/types';
import { v4 as uuidv4 } from 'uuid';
import { PlayerAdapter } from './adapter/types';
import { getAutoplayDetection } from './autoplay/platform-autoplay-detection';
import { clientCapabilities } from './capability/client-capabilities';
import { PlayerEvent } from './event-emitter/events';
import { PlayerMetric } from './event-emitter/player-metric';
import { FoundationErrorEvent, MetricEvent } from './event-emitter/unfiltered';
import { FoundationPlayer } from './foundation-player';
import { PlayerConfig } from './player-config';
import { PlayerContainer } from './player-container';
import { PlayerPlugin } from './plugin/player-plugin';

export type CreatePlayerOptions = {
  /**
   * The configuration for the player.
   */
  playerConfig?: PlayerConfig;

  /**
   * Plugins the player should use.
   * @internal
   */
  playerPlugins?: PlayerPlugin[];
};

export type RootPlayerFactory = (
  playerContainer: PlayerContainer,
  playerConfig: PlayerConfig,
  playerId: string,
  playerPlugins: PlayerPlugin[],
) => Promise<PlayerAdapter>;

export interface RootPlayerExport {
  createAdapter: RootPlayerFactory;
}

export async function createPlayer(
  rootPlayerExport: RootPlayerExport,
  options: CreatePlayerOptions,
  userConfig: LoaderConfig,
  remoteConfig: unknown,
  playerContainer: PlayerContainer,
): Promise<FoundationPlayer> {
  const logger = getLogger('player.core.createPlayer');
  // keep this so we get a readable error if bundling issues occur
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!rootPlayerExport.createAdapter) {
    throw new FoundationErrorEvent(PlayerEvent.Error, {
      message: 'No root player specified',
      code: PlayerError.PLAYER_DEPENDENCY_MISSING_52120.code,
    });
  }

  // keep this so we get a readable error if bundling issues occur
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!options) {
    throw new FoundationErrorEvent(PlayerEvent.Error, {
      message: 'Invalid player options',
      code: PlayerError.PLAYERCONFIG_PROBLEM_34000.code,
    });
  }

  const playerCreateStart = performance.now();
  const { playerConfig: initialPlayerConfig, playerPlugins = [] } = options;
  let playerConfig = initialPlayerConfig || {};

  const playerId = (playerConfig.playerId && playerConfig.playerId.trim()) || uuidv4();
  playerConfig.forceOneVideoElement ||= isVodafoneVTV();

  for (const plugin of playerPlugins) {
    if (plugin.beforeCreate) {
      playerConfig = await plugin.beforeCreate(playerConfig, playerId);
    }
  }

  const playerAdapter = await rootPlayerExport.createAdapter(playerContainer, playerConfig, playerId, playerPlugins);
  const autoplayDetection = getAutoplayDetection();
  const clientSupport = await clientCapabilities(playerContainer.getVideoElement() as HTMLVideoElement);

  const player = new FoundationPlayer({
    playerAdapter,
    playerContainer,
    playerPlugins,
    playerConfig,
    playerId,
    autoplayDetection,
    clientSupport,
    userConfig,
    remoteConfig,
  });

  await Promise.all(
    playerPlugins.map(async (plugin) => {
      plugin.setPlayer(player);

      if (plugin.playerInitialized) {
        try {
          await plugin.playerInitialized();
        } catch (e) {
          logger.error('Player initialized hook failed for plugin', {
            plugin,
            error: e,
          });
        }
      }
    }),
  );

  const playerCreateEnd = performance.now();
  player.dispatchEvent(
    new MetricEvent(PlayerMetric.PlayerCreation, { start: playerCreateStart, end: playerCreateEnd }),
  );

  return player;
}
